// src/pages/couriers/components/ColumnMappingsModal.jsx
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import {
  Map,
  X,
  Plus,
  Edit2,
  Trash2,
  Save,
  RefreshCw,
  FileText,
  Upload,
  Check
} from 'lucide-react';
import {
  getColumnMappingOverrides,
  createColumnMappingOverride,
  updateColumnMappingOverride,
  deleteColumnMappingOverride
} from '../../../services/api/businessCourier';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './ColumnMappingsModal.module.css';

const EMPTY_FORM = {
  mappingName: '',
  mappingConfig: '',
  notes: '',
  isActive: true
};

const ColumnMappingsModal = ({ isOpen, onClose, courier }) => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();

  const [invoiceMappings, setInvoiceMappings] = useState([]);
  const [uploadMappings, setUploadMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && courier) {
      loadMappings();
    }
  }, [isOpen, courier]);

  const loadMappings = async () => {
    setIsLoading(true);
    setEditingId(null);
    setEditingType(null);
    try {
      const data = await getColumnMappingOverrides(courier.id);
      setInvoiceMappings(data.mappings.filter(m => m.mappingType === 'courier_invoice'));
      setUploadMappings(data.mappings.filter(m => m.mappingType === 'upload'));
    } catch (err) {
      showError(t('couriers.mappings.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const startCreate = (mappingType) => {
    setEditingId(mappingType === 'courier_invoice' ? 'new-invoice' : 'new-upload');
    setEditingType(mappingType);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const startEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditingType(mapping.mappingType);
    setForm({
      mappingName: mapping.mappingName,
      mappingConfig: JSON.stringify(mapping.mappingConfig, null, 2),
      notes: mapping.notes || '',
      isActive: mapping.isActive
    });
    setFormErrors({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingType(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!form.mappingName.trim()) {
      errors.mappingName = t('couriers.mappings.nameRequired');
    }
    try {
      const parsed = JSON.parse(form.mappingConfig);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.mappingConfig = t('couriers.mappings.configMustBeObject');
      }
    } catch {
      errors.mappingConfig = t('couriers.mappings.invalidJson');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    let parsedConfig;
    try {
      parsedConfig = JSON.parse(form.mappingConfig);
    } catch {
      return;
    }

    setIsSubmitting(true);
    try {
      const isNew = editingId === 'new-invoice' || editingId === 'new-upload';
      if (isNew) {
        await createColumnMappingOverride(courier.id, {
          mappingType: editingType,
          mappingName: form.mappingName.trim(),
          mappingConfig: parsedConfig,
          notes: form.notes.trim() || null
        });
        success(t('couriers.mappings.createSuccess'));
      } else {
        await updateColumnMappingOverride(courier.id, editingId, {
          mappingName: form.mappingName.trim(),
          mappingConfig: parsedConfig,
          notes: form.notes.trim() || null,
          isActive: form.isActive
        });
        success(t('couriers.mappings.updateSuccess'));
      }
      cancelEdit();
      await loadMappings();
    } catch (err) {
      showError(err.response?.data?.message || t('couriers.mappings.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (mapping) => {
    const confirmed = await confirm(t('couriers.mappings.deleteConfirm'));
    if (!confirmed) return;
    try {
      await deleteColumnMappingOverride(courier.id, mapping.id);
      success(t('couriers.mappings.deleteSuccess'));
      await loadMappings();
    } catch (err) {
      showError(err.response?.data?.message || t('couriers.mappings.deleteError'));
    }
  };

  const renderForm = () => (
    <div className={styles.inlineForm}>
      <div className={styles.formGroup}>
        <label>{t('couriers.mappings.mappingName')}</label>
        <input
          type="text"
          value={form.mappingName}
          onChange={e => setForm(p => ({ ...p, mappingName: e.target.value }))}
          placeholder={t('couriers.mappings.mappingNamePlaceholder')}
          className={`${styles.textInput} ${formErrors.mappingName ? styles.inputError : ''}`}
        />
        {formErrors.mappingName && <span className={styles.errorText}>{formErrors.mappingName}</span>}
      </div>

      <div className={styles.formGroup}>
        <label>{t('couriers.mappings.mappingConfig')}</label>
        <small className={styles.fieldHint}>{t('couriers.mappings.mappingConfigHint')}</small>
        <textarea
          className={`${styles.jsonTextarea} ${formErrors.mappingConfig ? styles.inputError : ''}`}
          value={form.mappingConfig}
          onChange={e => setForm(p => ({ ...p, mappingConfig: e.target.value }))}
          rows={14}
          spellCheck={false}
          placeholder={'{\n  "version": "1.0",\n  "columnMappings": { ... },\n  "headerRowIndex": 1,\n  "dataStartRowIndex": 2\n}'}
        />
        {formErrors.mappingConfig && <span className={styles.errorText}>{formErrors.mappingConfig}</span>}
      </div>

      <div className={styles.formGroup}>
        <label>{t('couriers.mappings.notes')}</label>
        <textarea
          value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          rows={2}
          placeholder={t('couriers.mappings.notesPlaceholder')}
          className={styles.notesTextarea}
        />
      </div>

      {editingId !== 'new-invoice' && editingId !== 'new-upload' && (
        <div className={styles.formGroup}>
          <label className={styles.toggleLabel}>
            <span>{t('couriers.mappings.activeStatus')}</span>
            <div className={styles.toggleWrapper}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
                className={styles.toggleInput}
              />
              <span className={styles.toggleSlider}></span>
            </div>
          </label>
        </div>
      )}

      <div className={styles.formActions}>
        <button onClick={cancelEdit} className={styles.secondaryButton} disabled={isSubmitting}>
          {t('couriers.cancel')}
        </button>
        <button onClick={handleSave} className={styles.primaryButton} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <RefreshCw size={14} className={styles.spinning} />
              {t('couriers.saving')}
            </>
          ) : (
            <>
              <Save size={14} />
              {t('couriers.mappings.save')}
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderMappingSection = (type, typeLabel, icon, mappingList) => {
    const existing = mappingList[0] || null;
    const isCreating = editingId === (type === 'courier_invoice' ? 'new-invoice' : 'new-upload');
    const isEditingThis = existing && editingId === existing.id;

    return (
      <div className={styles.mappingSection}>
        <div className={styles.sectionHeader}>
          {icon}
          <h3>{typeLabel}</h3>
        </div>

        {existing && !isEditingThis && (
          <div className={styles.mappingCard}>
            <div className={styles.mappingCardInfo}>
              <span className={styles.mappingName}>{existing.mappingName}</span>
              <span className={`${styles.badge} ${existing.isActive ? styles.activeBadge : styles.inactiveBadge}`}>
                {existing.isActive ? (
                  <><Check size={10} /> {t('couriers.activeStatus')}</>
                ) : (
                  t('couriers.inactiveStatus')
                )}
              </span>
            </div>
            {existing.notes && <p className={styles.mappingNotes}>{existing.notes}</p>}
            <div className={styles.mappingActions}>
              <button
                onClick={() => startEdit(existing)}
                className={styles.iconButton}
                title={t('couriers.mappings.edit')}
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => handleDelete(existing)}
                className={`${styles.iconButton} ${styles.dangerIcon}`}
                title={t('couriers.mappings.delete')}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        )}

        {isEditingThis && renderForm()}

        {!existing && !isCreating && (
          <div className={styles.emptyMapping}>
            <p>{t('couriers.mappings.noOverride')}</p>
            <button onClick={() => startCreate(type)} className={styles.secondaryButton}>
              <Plus size={14} />
              {t('couriers.mappings.createOverride')}
            </button>
          </div>
        )}

        {isCreating && renderForm()}
      </div>
    );
  };

  if (!isOpen || !courier) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            <Map size={20} />
            {t('couriers.mappings.modalTitle')} — {courier.accountName}
          </h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          <p className={styles.modalDescription}>
            {t('couriers.mappings.modalDescription')}
          </p>

          {isLoading ? (
            <div className={styles.loadingState}>
              <RefreshCw size={24} className={styles.spinning} />
            </div>
          ) : (
            <>
              {renderMappingSection(
                'courier_invoice',
                t('couriers.mappings.invoiceType'),
                <FileText size={16} />,
                invoiceMappings
              )}
              {renderMappingSection(
                'upload',
                t('couriers.mappings.uploadType'),
                <Upload size={16} />,
                uploadMappings
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.secondaryButton}>
            {t('couriers.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnMappingsModal;
